import { useState, useMemo, useRef } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { PieChart } from "@/components/mortgage/PieChart"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"
import { useDebounce } from "@/MyCode/hooks/useDebounce"

interface PaymentSchedule {
    month: number
    date: string
    year: number
    payment: number
    principal: number
    interest: number
    balance: number
}

interface YearGroup {
    year: number
    payments: PaymentSchedule[]
    totals: {
        payment: number
        principal: number
        interest: number
        endingBalance: number
    }
}

export function AmortizedLoanCalculator() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    const [loanAmount, setLoanAmount] = useState(100000)
    const [interestRate, setInterestRate] = useState(5.0)
    const [loanTerm, setLoanTerm] = useState(30)
    const [startMonth, setStartMonth] = useState(currentMonth)
    const [startYear, setStartYear] = useState(currentYear)
    const [extraMonthlyPay, setExtraMonthlyPay] = useState(0)
    const [scheduleView, setScheduleView] = useState<Set<string>>(new Set(["months"]))

    // Debounced values
    const debouncedLoanAmount = useDebounce(loanAmount, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedExtraMonthlyPay = useDebounce(extraMonthlyPay, 500)

    const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - 50 + i)

    const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ]

    const calculateLoan = useMemo(() => {
        const principal = debouncedLoanAmount
        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12

        if (monthlyRate === 0) {
            const monthlyPayment = principal / numberOfPayments
            return {
                monthlyPayment,
                totalPayment: monthlyPayment * numberOfPayments,
                totalInterest: 0,
            }
        }

        const monthlyPayment =
            (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1)

        const totalPayment = monthlyPayment * numberOfPayments
        const totalInterest = totalPayment - principal

        return {
            monthlyPayment,
            totalPayment,
            totalInterest,
        }
    }, [debouncedLoanAmount, debouncedInterestRate, debouncedLoanTerm])

    const formatDate = (monthNumber: number): { formatted: string; year: number } => {
        const date = new Date(startYear, startMonth - 1 + monthNumber - 1, 1)
        const month = monthNames[date.getMonth()]
        const year = date.getFullYear()
        return { formatted: `${month} ${year}`, year }
    }

    const paymentSchedule: PaymentSchedule[] = useMemo(() => {
        const schedule: PaymentSchedule[] = []
        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12
        let balance = debouncedLoanAmount
        const monthlyPayment = calculateLoan.monthlyPayment

        for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            let principal = monthlyPayment - interest
            let extraPrincipal = debouncedExtraMonthlyPay

            principal += extraPrincipal
            balance -= principal

            const dateInfo = formatDate(month)
            schedule.push({
                month,
                date: dateInfo.formatted,
                year: dateInfo.year,
                payment: monthlyPayment + extraPrincipal,
                principal,
                interest,
                balance: Math.max(0, balance),
            })
        }

        return schedule
    }, [
        debouncedLoanAmount,
        debouncedInterestRate,
        debouncedLoanTerm,
        calculateLoan.monthlyPayment,
        startMonth,
        startYear,
        debouncedExtraMonthlyPay,
    ])

    const yearGroups: YearGroup[] = useMemo(() => {
        const groups: { [year: number]: PaymentSchedule[] } = {}

        paymentSchedule.forEach((payment) => {
            if (!groups[payment.year]) {
                groups[payment.year] = []
            }
            groups[payment.year].push(payment)
        })

        return Object.keys(groups)
            .sort((a, b) => Number(a) - Number(b))
            .map((year) => {
                const payments = groups[Number(year)]
                const totals = payments.reduce(
                    (acc, payment) => ({
                        payment: acc.payment + payment.payment,
                        principal: acc.principal + payment.principal,
                        interest: acc.interest + payment.interest,
                        endingBalance: payment.balance,
                    }),
                    { payment: 0, principal: 0, interest: 0, endingBalance: 0 }
                )
                return {
                    year: Number(year),
                    payments,
                    totals,
                }
            })
    }, [paymentSchedule])

    const totalInterestPaid = paymentSchedule.reduce((sum, p) => sum + p.interest, 0)
    const totalPrincipalPaid = paymentSchedule.reduce((sum, p) => sum + p.principal, 0)
    const totalPaymentsMade = paymentSchedule.reduce((sum, p) => sum + p.payment, 0)
    const monthsToPayoff = paymentSchedule.length

    // Calculate pie chart data
    const pieChartData = useMemo(() => {
        // Total principal paid includes original loan amount + extra payments
        // Extra payments = total principal paid - original loan amount
        const extraPayments = totalPrincipalPaid - debouncedLoanAmount
        // Principal from regular payments = original loan amount
        const principalFromRegular = debouncedLoanAmount

        return [
            { label: "Principal Paid", value: principalFromRegular, color: "#3b82f6" },
            { label: "Total Interest", value: totalInterestPaid, color: "#ef4444" },
            ...(extraPayments > 0 ? [{ label: "Extra Payments", value: extraPayments, color: "#8b5cf6" }] : []),
        ]
    }, [debouncedLoanAmount, totalInterestPaid, totalPrincipalPaid])

    // Generate month options for select fields
    const monthOptions = monthNames.map((month, index) => ({
        label: month,
        value: String(index + 1)
    }))

    // Generate year options for select fields
    const yearSelectOptions = yearOptions.map(year => ({
        label: String(year),
        value: String(year)
    }))

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.loanAmount !== undefined) setLoanAmount(Number(data.loanAmount))
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.loanTerm !== undefined) setLoanTerm(Number(data.loanTerm))
        if (data.startMonth !== undefined) setStartMonth(Number(data.startMonth))
        if (data.startYear !== undefined) setStartYear(Number(data.startYear))
        if (data.extraMonthlyPay !== undefined) setExtraMonthlyPay(Number(data.extraMonthlyPay))
    }

    const steps: Step[] = [
        {
            id: "loan-details",
            title: "Loan Details",
            description: "Enter your loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanAmount",
                    label: "Loan Amount 111",
                    placeholder: "100000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanAmount: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "interestRate",
                    label: "Interest Rate (%)",
                    placeholder: "5.0",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    allowNegative: false,
                    incrementValues: [1, 0.1, 0.01],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTerm",
                    label: "Loan Term (years)",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "select",
                    name: "startMonth",
                    label: "Start Month",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: monthOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startMonth: 'required|string|in:1,2,3,4,5,6,7,8,9,10,11,12'
                    }
                },
                {
                    type: "select",
                    name: "startYear",
                    label: "Start Year",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: yearSelectOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startYear: 'required|string'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraMonthlyPay",
                    label: "Extra Monthly Payment",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        extraMonthlyPay: 'nullable|numeric|min:0'
                    }
                }
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Amortized Loan Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate your loan amortization schedule and see how extra payments affect your payoff timeline.
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            loanAmount: loanAmount,
                            interestRate: interestRate,
                            loanTerm: loanTerm,
                            startMonth: String(startMonth),
                            startYear: String(startYear),
                            extraMonthlyPay: extraMonthlyPay,
                        }}
                    />
                </motion.div>

                {/* Right Side - Summary Cards */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-4"
                >


                    {/* Pie Chart */}
                    <div className="bg-card border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4 text-center">Total Cost Breakdown</h3>
                        <PieChart data={pieChartData} size={300} />
                    </div>
                </motion.div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">

                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Monthly Payment</div>
                    <div className="text-2xl font-bold">
                        <AnimatedNumber value={calculateLoan.monthlyPayment + debouncedExtraMonthlyPay} prefix="$" decimals={2} />
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Interest</div>
                    <div className="text-2xl font-bold mb-1">
                        <AnimatedNumber value={totalInterestPaid} prefix="$" decimals={2} />
                    </div>
                    <div className="text-sm text-muted-foreground mb-1">Total Payments</div>
                    <div className="text-2xl font-bold">
                        <AnimatedNumber value={totalPaymentsMade} prefix="$" decimals={2} />
                    </div>
                </div>


                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Payoff Time</div>
                    <div className="text-2xl font-bold">
                        {Math.floor(monthsToPayoff / 12)} years {monthsToPayoff % 12} months
                    </div>
                </div>
            </div>

            {/* Payment Schedule */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="mt-6"
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">Payment Schedule</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                const newView = new Set(scheduleView)
                                if (newView.has("months")) {
                                    newView.delete("months")
                                } else {
                                    newView.add("months")
                                }
                                setScheduleView(newView)
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${scheduleView.has("months")
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                        >
                            Show Months
                        </button>
                        <button
                            onClick={() => {
                                const newView = new Set(scheduleView)
                                if (newView.has("years")) {
                                    newView.delete("years")
                                } else {
                                    newView.add("years")
                                }
                                setScheduleView(newView)
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${scheduleView.has("years")
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                        >
                            Show Years
                        </button>
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-muted">
                                {scheduleView.has("months") ? (
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Payment</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Principal</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Interest</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Balance</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold">Year</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Total Payment</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Principal</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Interest</th>
                                        <th className="px-4 py-3 text-right text-sm font-semibold">Ending Balance</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {scheduleView.has("months") ? (
                                    paymentSchedule.slice(0, 100).map((payment) => (
                                        <tr key={payment.month} className="border-t border-border hover:bg-muted/50">
                                            <td className="px-4 py-2 text-sm">{payment.month}</td>
                                            <td className="px-4 py-2 text-sm">{payment.date}</td>
                                            <td className="px-4 py-2 text-sm text-right">
                                                ${payment.payment.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-green-600">
                                                ${payment.principal.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-red-600">
                                                ${payment.interest.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right font-medium">
                                                ${payment.balance.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    yearGroups.map((group) => (
                                        <tr key={group.year} className="border-t border-border hover:bg-muted/50">
                                            <td className="px-4 py-2 text-sm font-medium">{group.year}</td>
                                            <td className="px-4 py-2 text-sm text-right">
                                                ${group.totals.payment.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-green-600">
                                                ${group.totals.principal.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-red-600">
                                                ${group.totals.interest.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right font-medium">
                                                ${group.totals.endingBalance.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    )
}
